import {
  Alert,
  Button,
  Descriptions,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tooltip,
  Typography,
  notification,
} from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import {
  DELIVERY_ITEM_LABELS,
  DELIVERY_STATUS_ATIVOS,
  DELIVERY_STATUS_LABELS,
  STATUS_ATIVOS,
  StatusTag,
  describeError,
  formatDate,
  formatDateTime,
  formatUsd,
} from "./status";

const COLUNAS_ENTREGA = [
  {
    title: "Destinatário",
    dataIndex: "destinatario_nome",
    key: "nome",
    render: (nome, entrega) => (
      <span>
        {nome}
        {entrega.filiais?.length > 1 && (
          <Text type="secondary" style={{ display: "block" }}>
            Junto com mais {entrega.filiais.length - 1} filial(is)
          </Text>
        )}
        {entrega.filiais_sem_relatorio?.length > 0 && (
          <Text type="secondary" style={{ display: "block" }}>
            Sem relatório: {entrega.filiais_sem_relatorio.join(", ")}
          </Text>
        )}
      </span>
    ),
  },
  { title: "Telefone", dataIndex: "telefone", key: "telefone", width: 120 },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 110,
    render: (s) => <StatusTag status={s} mapa={DELIVERY_ITEM_LABELS} />,
  },
  {
    title: "Tentativas",
    dataIndex: "tentativas",
    key: "tentativas",
    width: 90,
    align: "right",
  },
  {
    title: "Enviado",
    dataIndex: "finished_at",
    key: "finished_at",
    width: 150,
    render: formatDateTime,
  },
  {
    title: "Erro",
    dataIndex: "error_message",
    key: "erro",
    ellipsis: true,
  },
  {
    title: "Resposta do webhook",
    dataIndex: "resposta_corpo",
    key: "resposta",
    ellipsis: true,
    render: (v) => v || "—",
  },
];

const { Text } = Typography;

export default function RunDetailModal({
  runId,
  onClose,
  rotinas,
  filiais,
  podeEnviar,
}) {
  const { getAiReportRun, cancelAiReportRun, deliverAiReportRun } = useApi();
  const queryClient = useQueryClient();
  const { data: run, isLoading } = useQuery({
    queryKey: ["ai-report-run", runId],
    queryFn: () => getAiReportRun(runId),
    enabled: Boolean(runId),
    refetchInterval: (query) =>
      STATUS_ATIVOS.includes(query.state.data?.status) ||
      DELIVERY_STATUS_ATIVOS.includes(query.state.data?.entrega_status)
        ? 3000
        : false,
    staleTime: 0,
  });

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(run.response_text || "");
      notification.success({ message: "Relatório copiado." });
    } catch {
      notification.error({ message: "Não foi possível copiar." });
    }
  };

  const cancelar = async () => {
    try {
      await cancelAiReportRun(run.id);
      notification.success({ message: "Execução cancelada." });
      queryClient.invalidateQueries({ queryKey: ["ai-report-runs"] });
      queryClient.invalidateQueries({ queryKey: ["ai-report-run", runId] });
    } catch (err) {
      notification.error({
        message: "Não foi possível cancelar.",
        description: describeError(err, ""),
      });
    }
  };

  const enviar = async () => {
    try {
      const resultado = await deliverAiReportRun(run.id);
      notification.success({
        message: `Envio enfileirado para ${resultado.entregas.length} destinatário(s).`,
        description: "O dispatcher envia no próximo ciclo, em até 5 minutos.",
      });
      queryClient.invalidateQueries({ queryKey: ["ai-report-runs"] });
      queryClient.invalidateQueries({ queryKey: ["ai-report-run", runId] });
    } catch (err) {
      notification.error({
        message: "Não foi possível enfileirar o envio.",
        description: describeError(err, ""),
      });
    }
  };

  const entregas = run?.entregas || [];
  const jaEnviou = entregas.length > 0;
  const ativo = run && STATUS_ATIVOS.includes(run.status);

  return (
    <Modal
      open={Boolean(runId)}
      title={run ? `Execução #${run.id}` : "Execução"}
      onCancel={onClose}
      width={860}
      footer={[
        run?.status === "pendente" && (
          <Button key="cancel" danger onClick={cancelar}>
            Cancelar execução
          </Button>
        ),
        podeEnviar && run?.status === "concluido" && (
          <Popconfirm
            key="deliver"
            title={jaEnviou ? "Reenviar relatório?" : "Enviar relatório?"}
            description={
              entregas.length
                ? `Destinatários: ${entregas.map((e) => e.telefone).join(", ")}`
                : "As mensagens vão para os destinatários cadastrados na rotina."
            }
            okText="Enviar"
            cancelText="Cancelar"
            disabled={!run?.pode_enviar}
            onConfirm={enviar}>
            <Tooltip title={run?.pode_enviar ? "" : run?.motivo_bloqueio}>
              <Button disabled={!run?.pode_enviar}>
                {jaEnviou ? "Reenviar por WhatsApp" : "Enviar por WhatsApp"}
              </Button>
            </Tooltip>
          </Popconfirm>
        ),
        run?.response_text && (
          <Button key="copy" onClick={copiar}>
            Copiar relatório
          </Button>
        ),
        <Button key="close" type="primary" onClick={onClose}>
          Fechar
        </Button>,
      ]}>
      {isLoading || !run ? (
        <Text type="secondary">Carregando…</Text>
      ) : (
        <div>
          {ativo && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Aguardando o dispatcher (a cada 5 minutos). Esta janela atualiza sozinha."
            />
          )}
          {run.entrega_suprimida && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Entrega suprimida pelo guardrail — revise antes de repassar."
              description={(run.guardrail_findings || [])
                .map((f) => `${f.tipo}: ${f.valor}`)
                .join("; ")}
            />
          )}
          {run.error_message && !run.entrega_suprimida && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={run.error_message}
            />
          )}
          <Descriptions size="small" column={3} bordered>
            <Descriptions.Item label="Status">
              <StatusTag status={run.status} />
            </Descriptions.Item>
            <Descriptions.Item label="Rotina">
              {rotinas?.[run.routine_id] || run.routine_id}
            </Descriptions.Item>
            <Descriptions.Item label="Filial">
              {filiais?.[run.branch_id] || run.branch_id}
            </Descriptions.Item>
            <Descriptions.Item label="Período">
              {formatDate(run.periodo_inicio)} → {formatDate(run.periodo_fim)}{" "}
              (fim exclusivo)
            </Descriptions.Item>
            <Descriptions.Item label="Origem">{run.origem}</Descriptions.Item>
            <Descriptions.Item label="Tentativas">
              {run.tentativas}
            </Descriptions.Item>
            <Descriptions.Item label="Modelo">{run.modelo}</Descriptions.Item>
            <Descriptions.Item label="Prompt">
              {run.prompt_version?.template} v{run.prompt_version?.version}
            </Descriptions.Item>
            {run.pedido && (
              <Descriptions.Item label="Pedido" span={3}>
                <span style={{ whiteSpace: "pre-wrap" }}>{run.pedido}</span>
              </Descriptions.Item>
            )}
            {run.entendimento && (
              <Descriptions.Item label="Entendimento" span={3}>
                {run.entendimento}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Custo">
              {formatUsd(run.custo_usd)}
            </Descriptions.Item>
            <Descriptions.Item label="Tokens">
              {run.input_tokens ?? "—"} / {run.output_tokens ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Latência">
              {run.latencia_ms
                ? `${(run.latencia_ms / 1000).toFixed(1)} s`
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Criado">
              {formatDateTime(run.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Entrega">
              <StatusTag
                status={run.entrega_status}
                mapa={DELIVERY_STATUS_LABELS}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Finalizado" span={2}>
              {formatDateTime(run.finished_at)}
            </Descriptions.Item>
          </Descriptions>
          {entregas.length > 0 && (
            <Space
              direction="vertical"
              style={{ width: "100%", marginTop: 16 }}>
              <Text strong>Entregas</Text>
              <Text type="secondary">
                O webhook confirma o recebimento da chamada, não a entrega no
                WhatsApp — não há retorno de status depois disso.
              </Text>
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                columns={COLUNAS_ENTREGA}
                dataSource={entregas}
              />
            </Space>
          )}
          {run.response_text && (
            <Space
              direction="vertical"
              style={{ width: "100%", marginTop: 16 }}>
              <Text strong>Relatório</Text>
              <div
                style={{
                  background: "#e7ffdb",
                  borderRadius: 10,
                  padding: 14,
                  whiteSpace: "pre-wrap",
                  fontSize: 14,
                  lineHeight: 1.5,
                  maxHeight: 460,
                  overflow: "auto",
                }}>
                {run.response_text}
              </div>
            </Space>
          )}
        </div>
      )}
    </Modal>
  );
}
